/* ==========================================================
   RANGREZA THEME — theme.js
   Cart Drawer, Mobile Menu, Search, Animations, Product
   ========================================================== */

'use strict';

/* ----------------------------------------------------------
   UTILITIES
   ---------------------------------------------------------- */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function formatMoney(cents, currencyCode) {
  const amount = (cents / 100).toFixed(2);
  if (currencyCode === 'PKR') return `Rs. ${Number(amount).toLocaleString('en-PK')}`;
  return `${currencyCode} ${amount}`;
}

function debounce(fn, delay = 300) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
}

/* ----------------------------------------------------------
   FLASH MESSAGE
   ---------------------------------------------------------- */
function showFlash(msg, duration = 3000) {
  let el = $('#rangreza-flash');
  if (!el) {
    el = document.createElement('div');
    el.id = 'rangreza-flash';
    el.className = 'flash-message';
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.classList.add('is-visible');
  setTimeout(() => el.classList.remove('is-visible'), duration);
}

/* ----------------------------------------------------------
   CART DRAWER
   ---------------------------------------------------------- */
class CartDrawer {
  constructor() {
    this.drawer  = $('#cart-drawer');
    this.overlay = $('#cart-drawer-overlay');
    this.body    = $('#cart-drawer-body');
    this.countEl = $$('.cart-count');
    this.subtotalEl = $('#cart-drawer-subtotal');
    this.footerEl = $('#cart-drawer-footer');
    this.shippingBarEl = $('#cart-shipping-bar');
    this._init();
  }

  _init() {
    if (!this.drawer) return;

    // Close triggers
    $('#cart-drawer-close')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

    // Open triggers — delegated
    document.addEventListener('click', e => {
      if (e.target.closest('[data-open-cart]')) {
        e.preventDefault();
        this.open();
      }
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.fetchCart();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async fetchCart() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }

  renderCart(cart) {
    // Update count badges
    this.countEl.forEach(el => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    const currency = cart.currency || window.Shopify?.currency?.active || 'PKR';

    // Update free shipping meter
    if (this.shippingBarEl) {
      const threshold = 500000;
      const total = cart.total_price;
      if (total >= threshold) {
        this.shippingBarEl.innerHTML = `
          <p class="shipping-bar__message shipping-bar__message--unlocked">
            ✨ You have unlocked <strong>FREE Nationwide Delivery</strong>!
          </p>
          <div class="shipping-bar__track"><div class="shipping-bar__progress" style="width: 100%;"></div></div>`;
      } else {
        const remaining = threshold - total;
        const progress = Math.min(100, Math.round((total / threshold) * 100));
        this.shippingBarEl.innerHTML = `
          <p class="shipping-bar__message">
            Add <strong>${formatMoney(remaining, currency)}</strong> more for <strong>FREE Nationwide Delivery</strong>
          </p>
          <div class="shipping-bar__track"><div class="shipping-bar__progress" style="width: ${progress}%;"></div></div>`;
      }
    }

    if (!this.body) return;

    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="btn btn--outline">Continue Shopping</a>
        </div>`;
      if (this.subtotalEl) this.subtotalEl.textContent = '';
      if (this.footerEl) this.footerEl.style.display = 'none';
      return;
    }

    if (this.footerEl) this.footerEl.style.display = '';

    let html = '';
    cart.items.forEach(item => {
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? item.variant_title : '';
      html += `
        <div class="cart-item" data-key="${item.key}">
          <div class="cart-item__image">
            <a href="${item.url}">
              <img src="${item.featured_image?.url || item.image}" alt="${item.title}" loading="lazy">
            </a>
          </div>
          <div class="cart-item__info">
            <a href="${item.url}" class="cart-item__name">${item.product_title}</a>
            ${variantTitle ? `<p class="cart-item__variant">${variantTitle}</p>` : ''}
            <p class="cart-item__price">${formatMoney(item.final_line_price, currency)}</p>
            <div class="cart-item__controls">
              <div class="qty-selector">
                <button class="qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease">&#8722;</button>
                <input class="qty-input" type="number" value="${item.quantity}" min="0" data-key="${item.key}" aria-label="Quantity">
                <button class="qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase">&#43;</button>
              </div>
              <button class="cart-item__remove" data-key="${item.key}" data-action="remove">Remove</button>
            </div>
          </div>
        </div>`;
    });

    this.body.innerHTML = html;

    if (this.subtotalEl) {
      this.subtotalEl.textContent = formatMoney(cart.total_price, currency);
    }

    // Bind qty controls
    this.body.addEventListener('click', e => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const key = btn.dataset.key;
      const action = btn.dataset.action;
      const row = btn.closest('.cart-item');
      const input = row.querySelector('.qty-input');
      let qty = parseInt(input.value);

      if (action === 'increase') qty++;
      else if (action === 'decrease') qty = Math.max(0, qty - 1);
      else if (action === 'remove') qty = 0;

      this.updateQty(key, qty);
    });

    this.body.addEventListener('change', e => {
      if (e.target.classList.contains('qty-input')) {
        const key = e.target.dataset.key;
        const qty = Math.max(0, parseInt(e.target.value) || 0);
        this.updateQty(key, qty);
      }
    });
  }

  async updateQty(key, qty) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart update error:', err);
    }
  }

  async addItem(variantId, qty = 1, properties = {}) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty, properties })
      });
      if (!res.ok) {
        const err = await res.json();
        showFlash(err.description || 'Could not add to cart');
        return null;
      }
      const item = await res.json();
      await this.fetchCart();
      this.open();
      return item;
    } catch (err) {
      console.error('Add to cart error:', err);
      return null;
    }
  }
}

/* ----------------------------------------------------------
   PRODUCT FORM (Product Page)
   ---------------------------------------------------------- */
class ProductForm {
  constructor(el, cart) {
    this.form      = el;
    this.cart      = cart;
    this.productId = el.dataset.productId;
    this.atcBtn    = el.querySelector('[data-atc-btn]');
    this.stickyAtc = $('#sticky-atc');
    this._init();
  }

  _init() {
    if (!this.form) return;

    // Variant buttons
    $$('[data-variant-btn]', this.form).forEach(btn => {
      btn.addEventListener('click', () => this._selectVariant(btn));
    });

    // Quantity
    $$('[data-qty-btn]', this.form).forEach(btn => {
      btn.addEventListener('click', () => this._adjustQty(btn));
    });

    // Submit
    this.form.addEventListener('submit', async e => {
    // Only intercept the normal Add to Cart button
     // Let Shopify's accelerated checkout handle Buy Now
      if (e.submitter !== this.atcBtn) return;

        e.preventDefault();

      const variantId = this.form.querySelector('[name="id"]')?.value;
      const qty = parseInt(
       this.form.querySelector('[name="quantity"]')?.value || 1
  );

  if (!variantId) return;

  this.atcBtn.disabled = true;
  this.atcBtn.textContent = 'Adding...';

  await this.cart.addItem(parseInt(variantId), qty);

  this.atcBtn.disabled = false;
  this.atcBtn.textContent = 'Add to Cart';
});

    // Sticky ATC
    if (this.stickyAtc) {
      const atcObserver = new IntersectionObserver(([entry]) => {
        this.stickyAtc.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0 });
      const sentinel = $('#atc-sentinel');
      if (sentinel) atcObserver.observe(sentinel);

      this.stickyAtc.querySelector('[data-sticky-atc-btn]')?.addEventListener('click', () => {
  this.form.requestSubmit(this.atcBtn);
});
    }
  }

  _selectVariant(btn) {
    const optionGroup = btn.dataset.optionGroup;
    $$(`[data-variant-btn][data-option-group="${optionGroup}"]`, this.form).forEach(b => {
      b.classList.remove('is-selected');
    });
    btn.classList.add('is-selected');

    // Resolve selected variant
    const selectedOptions = [];
    $$('[data-option-group]', this.form).forEach(group => {
      const active = group.querySelector('.is-selected');
      if (active) selectedOptions.push(active.dataset.variantOption);
    });

    let variants = [];
    const variantsScript = this.form.querySelector('[data-product-variants]');
    if (variantsScript) {
      try { variants = JSON.parse(variantsScript.textContent); } catch (e) {}
    } else if (this.form.dataset.variants) {
      try { variants = JSON.parse(this.form.dataset.variants); } catch (e) {}
    }

    const matched = variants.find(v =>
      v.options && v.options.every((opt, i) => opt === selectedOptions[i])
    );

    if (matched) {
      this.form.querySelector('[name="id"]').value = matched.id;
      const priceEl = $('#product-price');
      const compareEl = $('#product-compare-price');
      if (priceEl) priceEl.textContent = formatMoney(matched.price, window.Shopify?.currency?.active || 'PKR');
      if (compareEl && matched.compare_at_price > matched.price) {
        compareEl.textContent = formatMoney(matched.compare_at_price, window.Shopify?.currency?.active || 'PKR');
        compareEl.style.display = '';
      } else if (compareEl) {
        compareEl.style.display = 'none';
      }
      this.atcBtn.disabled = !matched.available;
      this.atcBtn.textContent = matched.available ? 'Add to Cart' : 'Sold Out';

      // Update gallery if variant has image
      if (matched.featured_image && window.rangrezaGallery) {
        window.rangrezaGallery.goToMediaPosition(matched.featured_image.position);
      }
    }
  }

  _adjustQty(btn) {
    const input = this.form.querySelector('[name="quantity"]');
    if (!input) return;
    const current = parseInt(input.value) || 1;
    if (btn.dataset.qtyBtn === 'inc') input.value = current + 1;
    else input.value = Math.max(1, current - 1);
  }
}

/* ----------------------------------------------------------
   QUICK ADD
   ---------------------------------------------------------- */
class QuickAdd {
  constructor(cart) {
    this.cart = cart;
    this._init();
  }

  _init() {
    document.addEventListener('click', async e => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const orig = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding...';

      await this.cart.addItem(parseInt(variantId));

      btn.disabled = false;
      btn.textContent = orig;
    });
  }
}

/* ----------------------------------------------------------
   MOBILE MENU
   ---------------------------------------------------------- */
class MobileMenu {
  constructor() {
    this.menu    = $('#mobile-menu');
    this.overlay = $('.mobile-menu__overlay');
    this.panel   = $('.mobile-menu__panel');
    this._init();
  }

  _init() {
    if (!this.menu) return;

    $('#mobile-menu-open')?.addEventListener('click', () => this.open());
    $('#mobile-menu-close')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

    // Submenu toggles
    $$('[data-submenu-toggle]', this.menu).forEach(toggle => {
      toggle.addEventListener('click', e => {
        e.preventDefault();
        const sub = toggle.nextElementSibling;
        if (sub) sub.classList.toggle('is-open');
      });
    });
  }

  open() {
    this.menu.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.menu.querySelector('[data-focus-first]')?.focus();
  }

  close() {
    this.menu.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

/* ----------------------------------------------------------
   SEARCH OVERLAY
   ---------------------------------------------------------- */
class SearchOverlay {
  constructor() {
    this.overlay = $('#search-overlay');
    this.input   = $('#search-input');
    this.results = $('#search-results');
    this._init();
  }

  _init() {
    if (!this.overlay) return;

    $$('[data-open-search]').forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });
    $('#search-close')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });

    this.input?.addEventListener('input', debounce(e => {
      this.search(e.target.value.trim());
    }, 400));

    this.overlay?.addEventListener('submit', e => {
      e.preventDefault();
      const q = this.input?.value?.trim();
      if (q) window.location.href = `/search?q=${encodeURIComponent(q)}&type=product`;
    });
  }

  open() {
    this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.input?.focus(), 100);
  }

  close() {
    this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async search(q) {
    if (!q || q.length < 2) {
      if (this.results) this.results.innerHTML = '';
      return;
    }
    try {
      const res  = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8`);
      const data = await res.json();
      this.renderResults(data.resources?.results?.products || []);
    } catch (err) {
      console.error('Search error:', err);
    }
  }

  renderResults(products) {
    if (!this.results) return;
    if (products.length === 0) {
      this.results.innerHTML = '<p class="text-muted" style="padding:20px 0">No results found.</p>';
      return;
    }
    const html = `<div class="search-results-grid">` +
      products.map(p => `
        <a href="${p.url}" class="product-card">
          <div class="product-card__media">
            <img src="${p.featured_image?.url || ''}" alt="${p.title}" class="product-card__img product-card__img--primary" loading="lazy">
          </div>
          <div class="product-card__info">
            <p class="product-card__title">${p.title}</p>
          </div>
        </a>`).join('') +
    `</div>`;
    this.results.innerHTML = html;
  }
}

/* ----------------------------------------------------------
   STICKY HEADER
   ---------------------------------------------------------- */
class StickyHeader {
  constructor() {
    this.header = $('.site-header');
    this._init();
  }

  _init() {
    if (!this.header || !this.header.classList.contains('is-transparent')) return;

    const onScroll = () => {
      const scrolled = window.scrollY > 60;
      this.header.classList.toggle('is-scrolled', scrolled);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
}

/* ----------------------------------------------------------
   SCROLL ANIMATIONS
   ---------------------------------------------------------- */
class ScrollAnimations {
  constructor() {
    const els = $$('[data-animate]');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    els.forEach(el => io.observe(el));
  }
}

/* ----------------------------------------------------------
   ACCORDION
   ---------------------------------------------------------- */
class Accordion {
  constructor() {
    $$('.accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => this._toggle(trigger));
    });
  }

  _toggle(trigger) {
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', !expanded);
    const content = trigger.nextElementSibling;
    if (content) content.classList.toggle('is-open', !expanded);
  }
}

/* ----------------------------------------------------------
   PRODUCT GALLERY — Thumbnails, Touch Swipe, Prev/Next
   ---------------------------------------------------------- */
class ProductGallery {
  constructor() {
    this.gallery = document.getElementById('product-gallery');
    this.thumbs  = $$('[data-thumb]');
    this.mainImg = $('#gallery-main-img');
    this.prevBtn = $('#gallery-prev');
    this.nextBtn = $('#gallery-next');
    this.counterEl = $('#gallery-current-idx');
    this.currentIndex = 0;

    if (!this.mainImg) return;
    window.rangrezaGallery = this;
    this._init();
  }

  _init() {
    // 1. Thumbnail click & tap
    this.thumbs.forEach((thumb, idx) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        this.goTo(idx);
      });
    });

    // 2. Prev & Next buttons
    this.prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.prev();
    });

    this.nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.next();
    });

    // 3. Touch Swiping on main image container for mobile
    const container = this.gallery?.querySelector('.product-gallery__main') || this.mainImg;
    let startX = 0;
    let startY = 0;

    container.addEventListener('touchstart', (e) => {
      if (e.touches && e.touches.length > 0) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }
    }, { passive: true });

    container.addEventListener('touchend', (e) => {
      if (e.changedTouches && e.changedTouches.length > 0) {
        const diffX = startX - e.changedTouches[0].clientX;
        const diffY = startY - e.changedTouches[0].clientY;
        if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY)) {
          if (diffX > 0) {
            this.next(); // swipe left -> next image
          } else {
            this.prev(); // swipe right -> previous image
          }
        }
      }
    }, { passive: true });

    // 4. Keyboard Arrow Navigation
    document.addEventListener('keydown', (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });
  }

  goTo(idx) {
    if (!this.thumbs.length) return;
    if (idx < 0) idx = this.thumbs.length - 1;
    if (idx >= this.thumbs.length) idx = 0;
    this.currentIndex = idx;

    const activeThumb = this.thumbs[idx];
    if (!activeThumb) return;

    // Update active thumb styling
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === idx));

    // Smooth switch image
    const newSrc = activeThumb.getAttribute('data-src');
    if (newSrc && this.mainImg) {
      this.mainImg.style.transition = 'opacity 0.2s ease';
      this.mainImg.style.opacity = '0.4';
      const img = new Image();
      img.onload = () => {
        this.mainImg.src = newSrc;
        this.mainImg.style.opacity = '1';
      };
      img.onerror = () => {
        this.mainImg.src = newSrc;
        this.mainImg.style.opacity = '1';
      };
      img.src = newSrc;
    }

    // Scroll active thumbnail into view smoothly
    activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

    // Update counter
    if (this.counterEl) {
      this.counterEl.textContent = idx + 1;
    }
  }

  next() {
    this.goTo(this.currentIndex + 1);
  }

  prev() {
    this.goTo(this.currentIndex - 1);
  }

  goToMediaPosition(pos) {
    const idx = this.thumbs.findIndex(t => t.dataset.thumb === String(pos));
    if (idx !== -1) this.goTo(idx);
  }
}

/* ----------------------------------------------------------
   NEWSLETTER FORM
   ---------------------------------------------------------- */
class NewsletterForm {
  constructor() {
    $$('[data-newsletter-form]').forEach(form => {
      form.addEventListener('submit', async e => {
        e.preventDefault();
        const email = form.querySelector('[name="contact[email]"]')?.value;
        if (!email) return;
        const btn = form.querySelector('[type="submit"]');
        const orig = btn.textContent;
        btn.textContent = 'Subscribing...';
        btn.disabled = true;

        try {
          // Shopify handles newsletter via the form's action
          const res = await fetch(form.action, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams(new FormData(form))
          });
          showFlash('Thank you for subscribing!');
          form.reset();
        } catch {
          showFlash('Subscription failed. Please try again.');
        }

        btn.textContent = orig;
        btn.disabled = false;
      });
    });
  }
}

/* ----------------------------------------------------------
   HERO PARALLAX (subtle)
   ---------------------------------------------------------- */
class HeroParallax {
  constructor() {
    this.hero = $('.hero__image');
    if (!this.hero || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    window.addEventListener('scroll', () => {
      const y = window.scrollY * 0.3;
      this.hero.style.transform = `translateY(${y}px)`;
    }, { passive: true });
  }
}

/* ----------------------------------------------------------
   INIT
   ---------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  const cart      = new CartDrawer();
  const quickAdd  = new QuickAdd(cart);
  const mobileMenu = new MobileMenu();
  const search    = new SearchOverlay();
  const header    = new StickyHeader();
  const anims     = new ScrollAnimations();
  const accordion = new Accordion();
  const gallery   = new ProductGallery();
  const newsletter = new NewsletterForm();
  const parallax  = new HeroParallax();

  // Product form (only on product pages)
  const productFormEl = $('[data-product-form]');
  if (productFormEl) new ProductForm(productFormEl, cart);
});
